/* eslint-disable */

import {
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Grid,
  Button,
  TextField,
  Dialog,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
} from '@mui/material';

import ButtonWithSpinner from '../ButtonWithSpinner';

import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import toastError from '../../toasts/toastError';
import useQueues from '../../hooks/useQueues';
import useUsers from '../../hooks/useUsers';
import { AuthContext } from '../../context/Auth/AuthContext';
import { getStyled } from './styles';

const TransferTicketModalCustom = ({
  modalOpen,
  onClose,
  ticketid,
  ticket,
}) => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const navigate = useNavigate();

  const { user } = useContext(AuthContext);

  const currentUser = useMemo(
    () => ticket?.user?.id || user.id,
    [ticket?.user?.id, user.id],
  );
  const currentQueue = useMemo(
    () => ticket?.queue?.id || user.queues[0]?.id,
    [ticket?.queue?.id, user.queues],
  );

  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserData, setSelectedUserData] = useState({});
  const [selectedQueue, setSelectedQueue] = useState('');
  const { findAll: findAllQueues } = useQueues();
  const { users } = useUsers();
  const [msgTransfer, setMsgTransfer] = useState('');

  useEffect(() => {
    const abortController = new AbortController();

    const loadQueues = async () => {
      try {
        const list = await findAllQueues();
        if (abortController.signal.aborted === false) {
          setQueues(list);
          setAllQueues(list);

          if (list.length > 0) {
            if (list.length === 1) {
              setSelectedQueue(list[0].id);
            } else if (currentQueue) {
              const currentQueueExists = list.some(
                (q) => q.id === currentQueue,
              );
              if (currentQueueExists) {
                setSelectedQueue(currentQueue);
              } else if (list[0]) {
                setSelectedQueue(list[0].id);
              }
            } else if (list[0]) {
              setSelectedQueue(list[0].id);
            }
          }
        }
      } catch (err) {
        if (abortController.signal.aborted === false) {
          toastError('Não foi possível carregar as filas. ', err);
        }
      }
    };

    if (modalOpen) {
      loadQueues();
    }

    return () => {
      abortController.abort();
    };
  }, [findAllQueues, modalOpen, currentQueue]);

  useEffect(() => {
    if (!selectedUserId || selectedUserId === '') {
      setQueues(allQueues);
      return;
    }

    if (!selectedUserData || !selectedUserData.queues) {
      return;
    }

    const commonQueues = allQueues.filter((queue) =>
      selectedUserData.queues.some((userQueue) => userQueue.id === queue.id),
    );

    if (commonQueues.length > 0) {
      setQueues(commonQueues);

      const currentQueueAvailable = commonQueues.some(
        (q) => q.id === selectedQueue,
      );

      if (!currentQueueAvailable) {
        setSelectedQueue(commonQueues[0]?.id || '');
      }
    } else {
      setQueues(allQueues);
    }
  }, [selectedUserData, allQueues, selectedUserId, selectedQueue]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSelectedQueue('');
      setSelectedUserId('');
      setMsgTransfer('');
      setSelectedUserData({});
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [modalOpen]);

  const handleMsgTransferChange = (event) => {
    setMsgTransfer(event.target.value);
  };

  const handleClose = () => {
    onClose();
  };

  const handleSaveTicket = useCallback(
    async (e) => {
      e.preventDefault();
      if (!ticketid) return;
      if (!selectedQueue || selectedQueue === '') return;

      setLoading(true);

      try {
        const data = {
          userId: selectedUserId === '' ? null : selectedUserId,
          queueId: selectedQueue,
          msgTransfer: msgTransfer,
          isTransfered: true,
          status: !selectedUserId
            ? 'pending'
            : ticket.isGroup
              ? 'group'
              : 'open',
          userTransferedId: currentUser,
          queueTransferedId: currentQueue,
          transferedToId: selectedUserId === '' ? null : selectedUserId,
          transferedAt: new Date(),
        };

        await api.put(`/tickets/${ticketid}`, data);

        setLoading(false);
        navigate(`/tickets/`);
        onClose();
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    },
    [
      ticketid,
      selectedQueue,
      selectedUserId,
      msgTransfer,
      ticket,
      currentUser,
      currentQueue,
      navigate,
      onClose,
    ],
  );

  const handleSelectUser = useCallback(
    async (userId) => {
      if (userId === '' || !userId) {
        setSelectedUserId('');
        setSelectedUserData({});
        setQueues(allQueues);
        return;
      }

      setSelectedUserId(userId);

      const abortController = new AbortController();

      try {
        const { data } = await api.get(`/users/${userId}`, {
          signal: abortController.signal,
        });

        if (abortController.signal.aborted === false) {
          setSelectedUserData(data);
        }
      } catch (err) {
        if (abortController.signal.aborted === false) {
          toastError('Erro ao carregar dados do usuário', err.message);
        }
      }

      return () => abortController.abort();
    },
    [allQueues],
  );

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      scroll='paper'
    >
      <DialogTitle sx={classes.dialogPaper}>
        {i18n.t('transferTicketModal.title')}
      </DialogTitle>
      <DialogContent dividers sx={classes.dialogPaper}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, xl: 6 }}>
            <FormControl variant='outlined' fullWidth>
              <InputLabel sx={theme.custom.label} shrink={true}>
                {i18n.t('transferTicketModal.fieldLabel')}
              </InputLabel>
              <Select
                sx={theme.custom.selectField}
                value={selectedUserId}
                onChange={async (e) => await handleSelectUser(e.target.value)}
                displayEmpty
                label={i18n.t('transferTicketModal.fieldLabel')}
                renderValue={(value) => {
                  if (value === '' || value === null || value === undefined) {
                    return 'Retornar para PENDENTES';
                  }
                  const selectedUser = users.find((user) => user.id === value);
                  return selectedUser ? selectedUser.name : '';
                }}
                MenuProps={theme.custom.menuProps}
              >
                <MenuItem value=''>Retornar para PENDENTES</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, xl: 6 }}>
            <FormControl variant='outlined' fullWidth>
              <InputLabel sx={theme.custom.label}>
                {i18n.t('transferTicketModal.fieldQueueLabel')}
              </InputLabel>
              <Select
                sx={theme.custom.selectField}
                value={selectedQueue}
                onChange={(e) => setSelectedQueue(e.target.value)}
                label={i18n.t('transferTicketModal.fieldQueuePlaceholder')}
                MenuProps={theme.custom.menuProps}
              >
                {queues.map((queue) => (
                  <MenuItem
                    key={queue.id}
                    value={queue.id}
                    style={classes.menuItem}
                  >
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2} style={{ marginTop: 10 }}>
          <Grid size={{ xs: 12, sm: 6, xl: 12 }}>
            <TextField
              sx={theme.custom.textField}
              label={i18n.t('transferTicketModal.msgTransfer')}
              value={msgTransfer}
              onChange={handleMsgTransferChange}
              variant='outlined'
              multiline
              minRows={4}
              maxRows={6}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ ...classes.dialogPaper, paddingRight: 3 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant='outlined'
          sx={theme.custom.cancelButton}
        >
          {i18n.t('transferTicketModal.buttons.cancel')}
        </Button>
        <ButtonWithSpinner
          variant='outlined'
          type='submit'
          sx={theme.custom.confirmButton}
          loading={loading}
          disabled={!selectedQueue}
          onClick={handleSaveTicket}
        >
          {i18n.t('transferTicketModal.buttons.ok')}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default TransferTicketModalCustom;
